<template>
  <v-container fluid class="pa-4">
    <v-row class="mb-6">
      <v-col cols="12" sm="8">
        <h1 class="text-h4 font-weight-bold primary--text">
          {{ $t('course_agreements_manage') }}
        </h1>
        <p class="text-subtitle2 grey--text mt-2">
          Gestionar campos con convenio y datos del coordinador
        </p>
      </v-col>
      <v-col cols="12" sm="4" class="text-sm-right">
        <v-btn
          color="primary"
          @click="openCreateDialog"
          :disabled="!canManage"
        >
          <v-icon left>mdi-plus</v-icon>
          {{ $t('create_agreement') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-alert
      v-if="!canManage"
      type="warning"
      dismissible
      class="mb-6"
    >
      No tienes permisos para gestionar campos con convenio. Solo Admin o Relaciones Públicas de Golf.
    </v-alert>

    <v-card class="elevation-2">
      <v-card-title>
        {{ $t('course_agreements') }}
        <v-spacer></v-spacer>
        <v-chip color="primary" small text-color="white">
          {{ agreements.length }}
        </v-chip>
      </v-card-title>

      <v-divider></v-divider>

      <v-progress-linear v-if="loading" indeterminate></v-progress-linear>

      <v-card-text>
        <div v-if="agreements.length === 0" class="text-center py-8 grey--text">
          {{ $t('no_agreements') }}
        </div>

        <v-row v-else>
          <v-col
            v-for="agreement in agreements"
            :key="agreement.id"
            cols="12"
            md="6"
            lg="4"
          >
            <CourseAgreementCard
              :agreement="agreement"
              show-edit
              show-delete
              @edit="openEditDialog(agreement)"
              @delete="confirmDelete(agreement)"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Form Dialog -->
    <CourseAgreementForm
      :show="showFormDialog"
      :agreement="selectedAgreement"
      @submit="handleFormSubmit"
      @close="closeFormDialog"
    />

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="error white--text">
          {{ $t('delete_agreement') }}
        </v-card-title>

        <v-card-text class="pa-6 pt-4">
          Estás a punto de eliminar: <strong>{{ agreementToDelete?.name }}</strong>
          <br><br>
          Esta acción no se puede deshacer.
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteDialog = false">
            {{ $t('cancelar') }}
          </v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            @click="handleDelete"
          >
            {{ $t('eliminar') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar de mensajes -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      bottom
      right
      timeout="4000"
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text small v-bind="attrs" @click="snackbar.show = false">
          {{ $t('cerrar') }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import CourseAgreementCard from './components/CourseAgreementCard.vue'
import CourseAgreementForm from './components/CourseAgreementForm.vue'

export default {
  name: 'CourseAgreementsAdmin',
  components: {
    CourseAgreementCard,
    CourseAgreementForm
  },
  data () {
    return {
      showFormDialog: false,
      showDeleteDialog: false,
      selectedAgreement: null,
      agreementToDelete: null,
      deleting: false,
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },
  computed: {
    ...mapGetters(['getProfile']),
    ...mapState('courseAgreements', {
      agreements: 'agreements',
      loading: 'loading'
    }),
    canManage () {
      const user = this.getProfile
      return user?.is_staff === true ||
             user?.is_superuser === true ||
             user?.permissions?.includes('golf_public_relations')
    }
  },
  mounted () {
    if (!this.canManage) {
      this.$router.push('/golf/course-agreements')
      return
    }
    this.loadAgreements()
  },
  methods: {
    ...mapActions('courseAgreements', ['fetchAll', 'create', 'update', 'delete']),
    async loadAgreements () {
      await this.fetchAll()
    },
    openCreateDialog () {
      this.selectedAgreement = null
      this.showFormDialog = true
    },
    openEditDialog (agreement) {
      this.selectedAgreement = agreement
      this.showFormDialog = true
    },
    closeFormDialog () {
      this.showFormDialog = false
      this.selectedAgreement = null
    },
    async handleFormSubmit (formData) {
      try {
        if (this.selectedAgreement) {
          await this.update({
            id: this.selectedAgreement.id,
            data: formData
          })
          this.showSnackbar(this.$t('agreement_updated'), 'success')
        } else {
          await this.create(formData)
          this.showSnackbar(this.$t('agreement_created'), 'success')
        }
        this.closeFormDialog()
        await this.loadAgreements()
      } catch (error) {
        this.showSnackbar(this.$t('agreement_error') + ': ' + error.message, 'error')
      }
    },
    confirmDelete (agreement) {
      this.agreementToDelete = agreement
      this.showDeleteDialog = true
    },
    async handleDelete () {
      if (!this.agreementToDelete) return

      this.deleting = true
      try {
        await this.delete(this.agreementToDelete.id)
        this.showSnackbar(this.$t('agreement_deleted'), 'success')
        this.showDeleteDialog = false
        await this.loadAgreements()
      } catch (error) {
        this.showSnackbar(this.$t('agreement_error') + ': ' + error.message, 'error')
      } finally {
        this.deleting = false
      }
    },
    showSnackbar (message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color
      }
    }
  }
}
</script>

<style scoped lang="scss">
</style>
